import { GetStaticPaths, GetStaticPropsContext } from "next"
import Head from "next/head"
import { useEffect, useReducer } from "react"
import { t } from "translations"

import Button from "@mui/material/Button"
import Skeleton from "@mui/material/Skeleton"
import Typography from "@mui/material/Typography"
import { styled } from "@mui/material/styles"

import { toPath } from "@nhi/utils"
import { getArchive } from "data/contentData/api/archive"
import { getFooter } from "data/contentData/api/content"
import { getMegamenu } from "data/contentData/api/megamenu"
import { IMegamenuCategory } from "data/contentData/interfaces/IMegamenuCategory"
import { IArchive } from "data/contentData/interfaces/mongodb/IArchive"
import { IContainerCard } from "data/contentData/interfaces/mongodb/IContainerCard"
import useQueryData from "data/hooks/useQueryData"
import { getKeyedArray } from "lib/getKeyedArray"

import ArchiveCard from "Components/Archive/ArchiveCard"
import {
    IArchiveState,
    archiveReducer
} from "Components/Archive/ArchiveReducer"
import TopFilterSelect, {
    LeftFilterSelect
} from "Components/Archive/FilterSelect"
import Icon from "Components/Icon/Icon"
import Layout from "Components/Layout/Layout"

const NUMBER_OF_ARTICLES_TO_RENDER = 20

export default function Archive({
    initialData,
    footer,
    megamenu
}: {
    initialData: IArchive
    footer: IContainerCard[]
    megamenu: IMegamenuCategory[]
}) {
    const archiveQueryPath: string = initialData.path

    const [state, dispatch] = useReducer(archiveReducer, {
        articles: initialData.articles,
        articleCount: initialData.articleCount,
        templates: initialData.templates,
        selectedFilter: "",
        skip: 0,
        shouldFetch: false
    } as IArchiveState)

    const {
        articles,
        articleCount,
        templates,
        selectedFilter,
        skip,
        shouldFetch
    } = state as IArchiveState

    const {
        data: archiveData,
        error,
        isLoading
    } = useQueryData(
        () =>
            //only trigger fetch clientside
            shouldFetch
                ? `${archiveQueryPath}-${selectedFilter}-${NUMBER_OF_ARTICLES_TO_RENDER}-${skip}`
                : null,
        () =>
            getArchive(
                archiveQueryPath,
                selectedFilter,
                NUMBER_OF_ARTICLES_TO_RENDER,
                skip
            )
    )

    //if clientside fetch is preformed update articlesArray
    useEffect(() => {
        if (archiveData)
            if (skip === 0)
                dispatch({
                    type: "new_articlesArray",
                    payload: {
                        articleCount: archiveData.articleCount,
                        articles: archiveData.articles
                    }
                })
            else {
                const newArray = [...articles, ...archiveData.articles]
                dispatch({
                    type: "update_articlesArray",
                    payload: newArray
                })
            }
    }, [archiveData, skip, articles])

    if (error) return null

    if (!state) return null

    /**
     * function for selecting a filter
     * @param template string name for selected filter
     */

    function selectFilter(template: string) {
        dispatch({
            type: "update_filter",
            payload: {
                selectedFilter: template,
                skip: 0
            }
        })
    }
    /**
     * resets the filter to default
     */

    function resetFilter() {
        dispatch({
            type: "reset_filter",
            payload: {
                articles: initialData.articles,
                articleCount: initialData.articleCount,
                template: initialData.template,
                skip: NUMBER_OF_ARTICLES_TO_RENDER
            }
        })
        window.scrollTo({ top: 0, left: 0, behavior: "smooth" })
    }

    /**
     * event handler that loads more articles
     * @param e event from click on button
     */
    function loadMoreArticles(
        e: React.MouseEvent<HTMLButtonElement, MouseEvent>
    ) {
        e.preventDefault()
        dispatch({
            type: "load_more",
            payload: skip + NUMBER_OF_ARTICLES_TO_RENDER
        })
    }

    return (
        <Layout
            footer={footer}
            megamenu={megamenu}>
            <Head>
                <title>
                    {t.archive.archive} | {t.siteTitle}
                </title>
                <meta
                    name="description"
                    content={`${t.archive.archive} | ${t.siteTitle}`}
                    key="desc"
                />
            </Head>

            <StyledWrapper>
                <LeftFilterSelect
                    templates={templates}
                    filter={selectedFilter}
                    selectFilter={selectFilter}
                    resetFilter={resetFilter}
                    isLoading={isLoading}
                />

                <StyledMain>
                    {isLoading ? (
                        <>
                            {getKeyedArray(20).map(key => (
                                <Skeleton
                                    key={key}
                                    height={140}
                                />
                            ))}
                        </>
                    ) : (
                        <>
                            <StyledHeader>
                                <Typography variant="subtitle1">
                                    <b>{t.archive.newsArchive}</b>
                                </Typography>
                                <Typography variant="subtitle2">
                                    {articleCount} {t.archive.articles}
                                </Typography>
                            </StyledHeader>
                            <StyledTopFilterSelectWrapper>
                                <TopFilterSelect
                                    templates={templates}
                                    filter={selectedFilter}
                                    selectFilter={selectFilter}
                                    resetFilter={resetFilter}
                                />
                            </StyledTopFilterSelectWrapper>
                            <ArchiveCard articles={articles} />
                            <StyledButtonWrapper>
                                <LoadMoreButton
                                    disabled={
                                        articles.length <
                                        NUMBER_OF_ARTICLES_TO_RENDER
                                            ? true
                                            : false
                                    }
                                    variant="outlined"
                                    onClick={e => loadMoreArticles(e)}>
                                    {articles.length <
                                    NUMBER_OF_ARTICLES_TO_RENDER ? (
                                        `${t.archive.noMoreArticles}`
                                    ) : (
                                        <>
                                            {t.archive.showMoreHits}{" "}
                                            <Icon name="add" />
                                        </>
                                    )}
                                </LoadMoreButton>
                            </StyledButtonWrapper>
                        </>
                    )}
                </StyledMain>
            </StyledWrapper>
        </Layout>
    )
}

export async function getStaticProps(ctx: GetStaticPropsContext) {
    const data = await getArchive(toPath(ctx.params?.archive), "")

    if (!data) return

    const footer = await getFooter()
    const megamenu = await getMegamenu()
    const revalidate = Number(process.env.REVALIDATE_STATIC_PROPS)

    return {
        props: {
            initialData: data,
            footer,
            megamenu
        },
        revalidate
    }
}

export const getStaticPaths: GetStaticPaths = async () => {
    return {
        paths: [],
        fallback: "blocking" // false or 'blocking' -> true or revalidate() to run when needed
    }
}

const StyledWrapper = styled("div")(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    display: "grid",
    gridTemplateColumns: "1fr",
    marginTop: theme.spacing(0),
    [theme.breakpoints.up("md")]: {
        gridTemplateColumns: "1fr 3fr",
        marginTop: theme.spacing(2)
    }
}))

const StyledMain = styled("main")(({ theme }) => ({
    padding: theme.spacing(0, 4, 4, 4),
    overflowY: "auto"
}))

const StyledHeader = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    marginTop: theme.spacing(0),
    marginBottom: theme.spacing(0),
    [theme.breakpoints.up("md")]: {
        marginTop: theme.spacing(4),
        marginBottom: theme.spacing(4)
    }
}))

const StyledTopFilterSelectWrapper = styled("div")(({ theme }) => ({
    display: "block",
    [theme.breakpoints.up("md")]: {
        display: "none"
    }
}))

const StyledButtonWrapper = styled("div")(({ theme }) => ({
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    paddingTop: theme.spacing(2),
    [theme.breakpoints.up("md")]: {
        float: "right"
    }
}))

const LoadMoreButton = styled(Button)(({ theme }) => ({
    gap: theme.spacing(1)
}))
